import { t, Trans } from '@lingui/macro'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  Paper,
  TableCell,
  Tooltip,
  Typography
} from '@mui/material'
import { dateFormat, portalLanguagesData } from 'app/appSettings'
import { getPortalTranslations } from 'app/services/sfAuth/sfData/sfTranslations'
import { muiDTNormalizedSearch } from 'app/utils/stringCompare'
import { Formik } from 'formik'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { ActionsColumnHeader } from '../common-components/ActionsColumnHeader'
import CustomDataTable from '../common-components/CustomDataTable'
import { muiTextLabels } from '../utilities/muiDataTablesTranslations'

import { setPortalTranslationData } from 'app/redux/actions/PortalTranslationDataActions'
import Loading from 'egret/components/EgretLoadable/Loading'
import { useDispatch, useSelector } from 'react-redux'
import { myI18n } from 'translation/I18nConnectedProvider'
import { CheckboxGroupWithFormik } from '../common-components/with-formik/CheckboxGroupWithFormik'

const TranslationConnectionsDialog = ({
  open,
  handleClose,
  saveTranslationConnection
}) => {
  const translations = useSelector(state => state.portalTranslationData)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const languageKeys = Object.keys(portalLanguagesData)
  const languageValues = Object.values(portalLanguagesData)
  const languages = languageKeys.map(key => portalLanguagesData[key].editKey)

  useEffect(() => {
    if (!translations) {
      setLoading(true)
      fetchData()
    }
  }, [])

  const fetchData = () => {
    return getPortalTranslations().then(
      result => {
        const mapped = []
        dispatch(setPortalTranslationData(
          result.map(item => {
            if (!mapped.includes(item.Translation_Id__c)) {
              mapped.push(item.Translation_Id__c)
            } { /* else {
              console.warn(
                'Duplicate translation found!',
                item.Translation_Id__c
              )
            }
              */ }
            const langQuery = {}
            languageValues.forEach(obj => {
              langQuery[obj.editKey] = item[obj.portalTranslationField]
            })
            return {
              id: item.Translation_Id__c,
              ...langQuery,
              found: false,
              comment: item.Translator_Comment__c,
              date: moment.utc(item.LastModifiedDate).format(dateFormat),
              actions: item.Translation_Id__c
            }
          })
        ))
        setLoading(false)
      },
      reject => {
        console.log(reject)
        enqueueSnackbar(<Trans>Error occured while loading!</Trans>, {
          variant: 'error'
        })
      }
    )
  }

  const getProperColumns = ({ values }) => {
    const hasLanguages = Boolean(
      values.displayedColumns.includes('en') &&
        values.displayedColumns.includes('fr')
    )
    const toRet = [
      {
        name: 'id',
        key: '1',
        label: myI18n._(t`Id`),
        options: {
          customHeadRender: (props, sort, sortData) => {
            return (
              <TableCell
                style={{ width: hasLanguages && '10%' }}
              >
                <Button
                  style={{ textTransform: 'none' }}
                  onClick={() => {
                    sort(0)
                  }}
                >
                  <Trans>Id</Trans>
                  {sortData?.name === 'id' && (
                    <Icon
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#757575',
                        margin: 4
                      }}
                    >
                      {sortData.direction === 'desc'
                        ? 'arrow_downward'
                        : 'arrow_upward'}
                    </Icon>
                  )}
                </Button>
              </TableCell>
            )
          },
          customBodyRender: (value, tableMeta, updateValue) => {
            return (
              <div>
                <Typography style={{ fontSize: 11 }}>{value}</Typography>
              </div>
            )
          }
        }
      }
    ]
    languageValues.forEach(obj => {
      const indexOf = toRet.length
      const lang = obj.editKey
      if (!values.displayedColumns.includes(lang)) {
        return
      }
      toRet.push({
        name: lang,
        label: portalLanguagesData[lang].labelTrans,
        options: {
          customHeadRender: (props, sort, sortData) => {
            return (
              <TableCell>
                <Button
                  style={{ textTransform: 'none' }}
                  onClick={() => {
                    sort(indexOf)
                  }}
                >
                  {portalLanguagesData[lang].labelTrans}
                  {sortData?.name === lang && (
                    <Icon
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#757575',
                        margin: 4
                      }}
                    >
                      {sortData.direction === 'desc'
                        ? 'arrow_downward'
                        : 'arrow_upward'}
                    </Icon>
                  )}
                </Button>
              </TableCell>
            )
          },
          customBodyRender: (value, tableMeta, updateValue) => {
            return (
              <div style={{ flex: 1, minWidth: 150 }}>
                <Typography style={{ fontSize: 12 }}>{value}</Typography>
              </div>
            )
          }
        }
      })
    })
    if (values.displayedColumns.includes('comments')) {
      const indexOf = toRet.length
      toRet.push({
        name: 'comment',
        label: myI18n._(t`Translator comment`),
        options: {
          customHeadRender: (props, sort, sortData) => {
            return (
              <TableCell style={{ width: hasLanguages && '20%' }}>
                <Button
                  style={{ textTransform: 'none' }}
                  onClick={() => {
                    sort(indexOf)
                  }}
                >
                  <Trans>Translator comment</Trans>
                  {sortData?.name === 'comment' && (
                    <Icon
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#757575',
                        margin: 4
                      }}
                    >
                      {sortData.direction === 'desc'
                        ? 'arrow_downward'
                        : 'arrow_upward'}
                    </Icon>
                  )}
                </Button>
              </TableCell>
            )
          },
          customBodyRender: (value, tableMeta, updateValue) => {
            return (
              <div style={{ flexShrink: 1, minWidth: 150 }}>
                <Typography style={{ fontSize: 12 }}>{value}</Typography>
              </div>
            )
          }
        }
      })
    }
    if (values.displayedColumns.includes('date')) {
      const indexOf = toRet.length
      toRet.push({
        name: 'date',
        label: myI18n._(t`Modified date`),
        options: {
          searchable: false,
          customHeadRender: (props, sort, sortData) => {
            return (
              <TableCell style={{ width: '10%' }}>
                <Button
                  style={{ textTransform: 'none' }}
                  onClick={() => {
                    sort(indexOf)
                  }}
                >
                  <Trans>Modified date</Trans>
                  {sortData?.name === 'date' && (
                    <Icon
                      style={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#757575',
                        margin: 4
                      }}
                    >
                      {sortData.direction === 'desc'
                        ? 'arrow_downward'
                        : 'arrow_upward'}
                    </Icon>
                  )}
                </Button>
              </TableCell>
            )
          },
          customBodyRender: (value, tableMeta, updateValue) => {
            return (
              <div>
                <Typography style={{ fontSize: 11, textAlign: 'center' }}>
                  {value}
                </Typography>
              </div>
            )
          }
        }
      })
    }
    toRet.push({
      name: 'actions',
      label: myI18n._(t`Actions`),
      searchable: false,
      options: {
        sort: false,
        customHeadRender: props => {
          return (
            <TableCell key={props.index} style={{ width: 150 }}>
              <ActionsColumnHeader />
            </TableCell>
          )
        },
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Grid container justifyContent='center' style={{ width: '100%' }}>
              <Tooltip title='ADD_CONNECTION'>
                <IconButton
                  onClick={() => {
                    saveTranslationConnection(value)
                    handleClose()
                  }}
                  size='large'
                >
                  <Icon>check</Icon>
                </IconButton>
              </Tooltip>
            </Grid>
          )
        }
      }
    })

    return toRet
  }

  return (
    (
      <Dialog
        open={open}
        fullWidth
        maxWidth='lg'
        scroll='paper'
        PaperProps={{
          style: { position: 'static' }
        }}
      >
        <DialogTitle>
          <Grid
      container
      direction='row'
      alignItems='center'
      justifyContent='space-between'
    >
      <Trans>CONNECTIONS_TITLE</Trans>
      <Tooltip title={<Trans>Close</Trans>}>
            <IconButton
              onClick={() => {
                handleClose()
              }}
              color='default'
              size='large'
            >
              <Icon>close</Icon>
            </IconButton>
          </Tooltip>
    </Grid>
        </DialogTitle>
        <DialogContent>
          <Formik
      initialValues={{
            id: '',
            displayedColumns: languages
          }}
    >
      {({ values, setValues }) => {
            return (
              <>
                <Paper style={{ padding: 20 }} elevation={6}>
                  <CheckboxGroupWithFormik
                    label={<Trans>Displayed columns</Trans>}
                    style={{ marginTop: 20, width: '100%' }}
                    name='displayedColumns'
                    options={[
                      ...Object.values(portalLanguagesData).map(obj => ({
                        label: obj.labelTrans,
                        value: obj.editKey
                      })),
                      {
                        label: <Trans>Translator comments</Trans>,
                        value: 'comments'
                      },
                      {
                        label: <Trans>Modified date</Trans>,
                        value: 'date'
                      }
                    ]}
                  />
                </Paper>

                {loading
                  ? (
                    <Loading />
                    )
                  : (
                    <CustomDataTable
                      title={
                        <Typography
                          style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            lineHeight: '27px',
                            letterSpacing: '-0.2px'
                          }}
                        >
                          <Trans>Portal Translations</Trans>
                        </Typography>
                      }
                      data={translations}
                      options={{
                        textLabels: muiTextLabels(myI18n),
                        filter: false,
                        print: false,
                        download: false,
                        viewColumns: false,
                        selectableRows: 'none',
                        customSearch: muiDTNormalizedSearch
                      }}
                      columns={getProperColumns({
                        values,
                        data: translations
                      })}
                    />
                    )}
              </>
            )
          }}
    </Formik>
        </DialogContent>
      </Dialog>
    )
  )
}

export default TranslationConnectionsDialog
